import {assert} from 'Asserts';
import {ProcessExecutionType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {TaskDefinitionVersionSpec, TaskSchemaSpec} from 'processes/dao/ProcessFlowchartConfigDAO';
import {ProcessFlowchartTaskSchemaNodeAdapter} from 'processes/graph-builder-processes/flowchart-viz/horizontal-flowchart/nodes/ProcessFlowchartNodeAdapter';
import {PLUGIN_REGISTRY_INSTANCE} from 'processes/graph-builder-processes/platform/PluginRegistry';

import {ProcessTaskSchemaWithoutFlowchartConfigGraphQL} from '../../../../../graphql-fragments/processes/schemas/__generated/ProcessTaskSchemaWithoutFlowchartConfigGraphQL';
import {getFieldInfoFromDataFlowEdgeHandle} from './flowchartIdentifierUtils';
import {
  ProcessFlowchartEdgeType,
  StaticTemplateFlowchart,
  TaskDefinitionVersionSpecEdge,
  TaskDefinitionVersionSpecWithTaskSchemaFlowchart,
} from './processFlowchartConfigEditorConstants';
import {ProcessTaskDefinitionVersionGraphQL} from './processFlowchartConfigEditorConstants';
import {
  isStaticTaskSchemaNodeData,
  TaskDefinitionVersionSpecWithTaskSchema,
} from './processFlowchartConfigEditorNodeTypes';

export function convertApplicationDataToSpec(
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL,
  executionType: ProcessExecutionType
): unknown {
  const applicationData = taskDefinitionVersion.applicationData;
  if (applicationData == null) {
    return null;
  }
  if (isInaccessible(applicationData)) {
    return null;
  }

  const adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> =
    PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(executionType);
  const convertedApplicationData =
    adapter.convertTaskDefinitionVersionGQLToApplicationData(taskDefinitionVersion);

  return convertedApplicationData;
}

/**
 * Convert a task definition version GQL to a task definition version spec with task schema.
 * This spec will contain the frontend application data for the task definition version, and will
 * later be converted to the network spec application data for saving changes.
 *
 * @param taskDefinitionVersion - The task definition version GQL to convert.
 * @param fullTaskSchema - The full task schema (looked up from allTaskSchemas) with complete field data.
 * @returns The converted task definition version spec with task schema and frontend application data.
 */
export function convertTaskDefinitionVersionToSpec(
  taskDefinitionVersion: ProcessTaskDefinitionVersionGraphQL,
  fullTaskSchema: ProcessTaskSchemaWithoutFlowchartConfigGraphQL
): TaskDefinitionVersionSpecWithTaskSchema {
  const taskDefinitionSchema = taskDefinitionVersion.processTaskSchema;
  assert(!isInaccessible(taskDefinitionSchema), 'Task definition schema is unexpectedly inaccessible', {
    taskDefinitionVersion,
    taskDefinitionSchema,
  });

  const taskSchema: TaskSchemaSpec = {
    id: taskDefinitionSchema.id,
    name: taskDefinitionSchema.name,
    executionType: taskDefinitionSchema.executionType,
    fields: fullTaskSchema.fields,
    outputSchema:
      fullTaskSchema.outputSchema != null
        ? {
            id: fullTaskSchema.outputSchema.id,
            name: fullTaskSchema.outputSchema.name,
            fields: fullTaskSchema.outputSchema.fields,
          }
        : null,
  };

  const applicationData = convertApplicationDataToSpec(
    taskDefinitionVersion,
    taskDefinitionSchema.executionType
  );

  return {
    clientId: taskDefinitionVersion.id,
    currentVersionId: taskDefinitionVersion.id,
    inputFieldDefinitions: taskDefinitionVersion.inputFieldDefinitions,
    outputFieldDefinitions: taskDefinitionVersion.outputFieldDefinitions,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskSchema.id,
      applicationData,
      taskSchema,
    },
  };
}

/**
 * Converts a frontend TaskDefinitionVersionSpecWithTaskSchema into a network spec TaskDefinitionVersionSpec.
 * This functionally removes the task schema from the spec, as it is not needed for updates.
 */
export function convertFrontendTaskDefinitionVersionSpecToNetworkSpec(
  taskDefinitionVersionSpec: TaskDefinitionVersionSpecWithTaskSchema<unknown>,
  flowchartConfigId: string
): TaskDefinitionVersionSpec {
  const adapter: ProcessFlowchartTaskSchemaNodeAdapter<unknown, unknown> =
    PLUGIN_REGISTRY_INSTANCE.getTaskSchemaNodeAdapter(
      taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.executionType
    );
  const networkApplicationData = adapter.convertTaskDefinitionVersionSpecToNetworkApplicationData(
    taskDefinitionVersionSpec,
    flowchartConfigId
  );

  return {
    clientId: taskDefinitionVersionSpec.clientId,
    currentVersionId: taskDefinitionVersionSpec.currentVersionId,
    newProcessTaskDefinitionVersion: {
      taskSchemaId: taskDefinitionVersionSpec.newProcessTaskDefinitionVersion.taskSchema.id,
      applicationData: networkApplicationData,
    },
  };
}

export function convertStaticFlowchartToSpecFlowchartWithTaskSchema(
  flowchart: StaticTemplateFlowchart
): TaskDefinitionVersionSpecWithTaskSchemaFlowchart {
  // nodeId -> TaskDefinitionVersionSpecWithTaskSchema
  const nodes: Record<string, TaskDefinitionVersionSpecWithTaskSchema<unknown>> = {};
  for (const node of flowchart.nodesStaticData) {
    if (isStaticTaskSchemaNodeData(node)) {
      nodes[node.id] = node.taskDefinitionVersionSpec;
    }
  }

  const edges: Array<TaskDefinitionVersionSpecEdge> = [];
  for (const edge of flowchart.edgesStaticData) {
    const edgeSpec: TaskDefinitionVersionSpecEdge = {
      fromNodeClientId: edge.source,
      toNodeClientId: edge.target,
      type: edge.type,
    };

    if (edge.type === ProcessFlowchartEdgeType.DATA_FLOW) {
      edgeSpec.fromFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.sourceHandle).fieldClientId;
      edgeSpec.toFieldClientId = getFieldInfoFromDataFlowEdgeHandle(edge.targetHandle).fieldClientId;
    }

    edges.push(edgeSpec);
  }

  return {
    nodes,
    edges,
  };
}
